import { uploadFileReq } from '@/shared/requests/upload-file';
import { addNotification } from '@/shared/states/notification';
import { useSocketData } from '@/shared/states/sockets';
import { useCallback, useEffect, useState } from 'react';
import { isValidFieldType } from './constants';
import DiscardAgentModal from './DiscardAgentModal';
import HeaderSection from './HeaderSection';
import {
  useCreateFrameworkMutation,
  useDeleteFrameworkMutation,
  usePublishFrameworkMutation,
  useSaveFrameworkDraftMutation,
  useSubmitFrameworkControlsMutation,
  useUpdateFrameworkSchemaMutation,
} from './hooks';
import Layout from './Layout';
import { useFrameworkWizardStore } from './states';
import StepFour from './StepFour';
import StepOne from './StepOne';
import StepThree from './StepThree';
import StepTwo from './StepTwo';
import SuccessScreen from './SuccessScreen';
import { FrameworkField } from './types';

export default function AddFrameworkWizard({
  onClose,
}: {
  onClose?: () => void;
}) {
  const {
    currentStep,
    totalSteps,
    setCurrentStep,
    validateStep,
    markStepCompleted,
    resetWizard,
    closeWizard,
    frameworkName,
    stepOneData,
    stepTwoData,
    stepThreeData,
    stepFourData,
    updateStepTwoData,
    updateStepFourData,
    updateStepOneData,
    setFirstStepValidationError,
    updateStepThreeData,
    pendingSessionTitle,
    setCreatedFrameworkValue,
  } = useFrameworkWizardStore();

  // Local state for discard modal
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [stepThreeProceedBlocked, setStepThreeProceedBlocked] = useState(false);
  const [isPublishCompleted, setIsPublishCompleted] = useState(false);

  // React Query mutations
  const createFrameworkMutation = useCreateFrameworkMutation();
  const saveFrameworkDraftMutation = useSaveFrameworkDraftMutation();
  const deleteFrameworkMutation = useDeleteFrameworkMutation();
  const publishFrameworkMutation = usePublishFrameworkMutation();
  const submitControlsMutation = useSubmitFrameworkControlsMutation();
  const updateSchemaMutation = useUpdateFrameworkSchemaMutation();

  // Add socket data for getting socket ID
  const socketData = useSocketData('main');

  // Helper function to sync fields with backend response
  const syncFieldsWithBackendResponse = (
    localFields: FrameworkField[],
    backendColumnTitles: Array<{ index: number; key: string; ui_key: string }>
  ) => {
    return localFields.map((localField, index) => {
      // Find matching backend field by index or key
      const backendField = backendColumnTitles.find(
        (bt) => bt.index === index || bt.key === localField.key
      );

      if (backendField) {
        return {
          ...localField,
          key: backendField.key, // Use backend key
          ui_key: backendField.ui_key, // Use backend ui_key (which might be capitalized differently)
        };
      }

      return localField;
    });
  };

  // Helper function to upload controls data - SIMPLIFIED FOR RWD PERMISSIONS
  const uploadControlsData = async (controls: any[]) => {
    try {
      if (!stepTwoData.controlsUrl) {
        throw new Error('Controls URL not found from Step 2');
      }

      // Use cleaned controls data instead of raw controls
      const cleanedControls = stepThreeData.getCleanedControls
        ? stepThreeData.getCleanedControls()
        : controls;

      // Create a JSON blob with the cleaned controls data
      const controlsBlob = new Blob(
        [JSON.stringify(cleanedControls, null, 2)],
        {
          type: 'application/json',
        }
      );

      // Upload directly to the controls URL (now has RWD permissions)
      await uploadFileReq(stepTwoData.controlsUrl, controlsBlob);

      return stepTwoData.controlsUrl;
    } catch (error) {
      console.error('Error uploading controls data:', error);
      throw error;
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <StepOne />;
      case 2:
        return <StepTwo />;
      case 3:
        return <StepThree setProceedBlocked={setStepThreeProceedBlocked} />;
      case 4:
        return <StepFour />;
      default:
        return <StepOne />;
    }
  };

  const handleStepClick = useCallback(
    (step: number) => {
      if (step <= currentStep || step === currentStep + 1) {
        setCurrentStep(step);
        // reset states to the step clicked
        if (step < 4) {
          updateStepFourData({
            responseSchemaUrl: undefined,
          });
        }
        if (step < 3) {
          updateStepThreeData({
            controls: [],
          });
        }
        if (step < 2) {
          // Reset step one data when going back to step 1
          updateStepTwoData({
            files: [],
            filledTemplateUrl: undefined,
            templateUrl: undefined,
            controlsUrl: undefined,
            schemaFields: [],
          });
          updateStepFourData({
            responseSchemaUrl: undefined,
          });

          setFirstStepValidationError(false);
        }
      }
    },
    [currentStep, setCurrentStep]
  );

  const handleProceed = useCallback(async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    markStepCompleted(currentStep);

    if (currentStep === 1) {
      // Check if framework already exists (user went back to step 1)
      if (stepTwoData.frameworkId && stepTwoData.schemaStepId) {
        // Framework exists - update schema instead of creating new
        updateSchemaMutation.mutate(
          {
            frameworkStepId: stepTwoData.schemaStepId,
            frameworkId: stepTwoData.frameworkId,
            fields: stepOneData.fields || [],
          },
          {
            onSuccess: (response) => {
              // Extract schema fields from stepOneData, but sync with backend response if available
              let schemaFields =
                stepOneData.fields?.map((field, index) => ({
                  index,
                  key: field.key,
                  ui_key: field.ui_key,
                })) || [];

              // If backend returned column titles, sync the fields
              if (response.data.columnTitles) {
                const syncedFields = syncFieldsWithBackendResponse(
                  stepOneData.fields || [],
                  response.data.columnTitles
                );

                // Update step one data with synced fields
                updateStepOneData({ fields: syncedFields });

                // Update schema fields with backend response
                schemaFields = response.data.columnTitles;
              }

              updateStepTwoData({
                templateUrl: response.data.templateUrl,
                schemaStepId: response.data.schemaStepId,
                controlsStepId: response.data.controlsStepId,
                schemaFields: schemaFields,
                // Clear controls URL as schema change invalidates previous controls
                controlsUrl: undefined,
                filledTemplateUrl: undefined, // User needs to re-upload
              });
              setCurrentStep(currentStep + 1);
            },
          }
        );
      } else {
        // No framework exists - create new one
        const payload = {
          name: frameworkName,
          stepOneData,
          stepTwoData,
          stepThreeData,
          stepFourData,
        };
        createFrameworkMutation.mutate(payload, {
          onSuccess: (response) => {
            // Extract schema fields from stepOneData, but sync with backend response if available
            let schemaFields =
              stepOneData.fields?.map((field, index) => ({
                index,
                key: field.key,
                ui_key: field.ui_key,
              })) || [];

            // If backend returned column titles, sync the fields
            if (response.data.columnTitles) {
              const syncedFields = syncFieldsWithBackendResponse(
                stepOneData.fields || [],
                response.data.columnTitles
              );

              // Update step one data with synced fields
              updateStepOneData({ fields: syncedFields });

              // Update schema fields with backend response
              schemaFields = response.data.columnTitles;
              setFirstStepValidationError(false);
            }

            // Save framework ID, template URL, step IDs, and schema fields for updates
            updateStepTwoData({
              frameworkId: response.data.id,
              templateUrl: response.data.templateUrl,
              schemaStepId: response.data.schemaStepId,
              controlsStepId: response.data.controlsStepId,
              schemaFields: schemaFields,
            });
            setCurrentStep(currentStep + 1);
          },
          onError: (error) => {
            setFirstStepValidationError(true);
          },
        });
      }
    } else if (currentStep === 2) {
      // Step 2: Submit filled template (always call API, even if no template uploaded)
      const payload = {
        name: frameworkName,
        stepOneData,
        stepTwoData, // This should now contain the frameworkId from Step 1
        stepThreeData,
        stepFourData,
      };

      saveFrameworkDraftMutation.mutate(payload, {
        onSuccess: (response) => {
          // Save controls URL if provided
          const schemaFields = stepTwoData.schemaFields
            ? [...stepTwoData.schemaFields]
            : [];
          if (response.data.controlsUrl) {
            updateStepTwoData({
              controlsUrl: response.data.controlsUrl,
              schemaFields: schemaFields,
            });
          }
          setCurrentStep(currentStep + 1);
        },
      });
    } else if (currentStep === 3) {
      // Step 3: Upload controls data then submit URL to API
      if (stepTwoData.frameworkId && stepThreeData.controls) {
        try {
          // Upload cleaned controls data and get the URL
          const controlsUrl = await uploadControlsData(stepThreeData.controls);

          submitControlsMutation.mutate(
            {
              frameworkId: stepTwoData.frameworkId,
              frameworkName: frameworkName,
              controlsUrl: controlsUrl, // Send the URL to the API
            },
            {
              onSuccess: (response) => {
                // If response includes response schema URL and step ID, save both for step 4
                if (response.data.responseSchemaUrl) {
                  updateStepFourData({
                    responseSchemaUrl: response.data.responseSchemaUrl,
                  });
                }
                setCurrentStep(currentStep + 1);
              },
              onError: (error) => {
                console.error('Failed to submit controls:', error);
                // Still proceed to next step even if submit fails
                setCurrentStep(currentStep + 1);
              },
            }
          );
        } catch (error) {
          console.error('Failed to upload controls data:', error);
          addNotification({
            type: 'error',
            message: 'Failed to save controls data',
          });
          // Still proceed to next step
          setCurrentStep(currentStep + 1);
        }
      } else {
        // No controls to submit, just proceed
        setCurrentStep(currentStep + 1);
      }
    } else if (currentStep === 4) {
      // Step 4: Publish framework

      // Validate required data before publishing
      if (!stepTwoData.frameworkId) {
        addNotification({
          type: 'error',
          message: 'Framework ID not found. Please complete previous steps.',
        });
        return;
      }

      if (!frameworkName.trim()) {
        addNotification({
          type: 'error',
          message: 'Framework name is required for publishing.',
        });
        return;
      }

      // Get socket ID for tracking publish completion
      const socketId = socketData?.socket?.id;
      if (!socketId) {
        addNotification({
          type: 'error',
          message:
            'Connection required for publishing. Please refresh and try again.',
        });
        return;
      }

      // Filter out incomplete fields from responseFields and update Azure blob before making API call
      const filteredData = stepFourData.responseFields?.filter((field) => {
        // If it's a newly added field (isFromStepThree), validate it
        if (field.isFromStepThree) {
          return isValidFieldType(field.ui_key) || isValidFieldType(field.key);
        }
        // Keep all original fields
        return true;
      }) || [];      // Update the response schema data in Azure blob to remove incomplete field columns
      if (
        stepFourData.responseSchemaUrl &&
        stepThreeData.controls &&
        stepThreeData.controls.length > 0
      ) {
        try {
          // Get the current response schema data
          const response = await fetch(stepFourData.responseSchemaUrl);
          const currentResponseSchemaData = await response.json();

          if (
            currentResponseSchemaData &&
            Array.isArray(currentResponseSchemaData) &&
            currentResponseSchemaData.length > 0
          ) {
            // Filter out columns for incomplete fields from the response schema data
            const fieldsToRemove =
              stepFourData.responseFields?.filter((field) => {
                // Remove fields that are editable, not from step three, and don't have a valid selection
                if (!field.isEditable || field.isFromStepThree) {
                  return false; // Don't remove these
                }

                // For editable fields that are not from step three, check if they have a valid selection
                return !(
                  isValidFieldType(field.ui_key) || isValidFieldType(field.key)
                );
              }) || [];

            // Remove the incomplete field columns from each row
            const updatedResponseSchemaData = currentResponseSchemaData.map(
              (item: any) => {
                const updatedItem = { ...item };
                fieldsToRemove.forEach((fieldToRemove) => {
                  // Remove columns that match the incomplete field keys
                  Object.keys(updatedItem).forEach((key) => {
                    if (
                      key === fieldToRemove.key ||
                      key === fieldToRemove.ui_key
                    ) {
                      delete updatedItem[key];
                    }
                  });
                });
                return updatedItem;
              }
            );

            // Upload the updated data to Azure blob
            const responseSchemaBlob = new Blob(
              [JSON.stringify(updatedResponseSchemaData, null, 2)],
              {
                type: 'application/json',
              }
            );
            await uploadFileReq(
              stepFourData.responseSchemaUrl,
              responseSchemaBlob
            );
          }
        } catch (error) {
          console.error(
            'Failed to update response schema before publish:',
            error
          );
          // Continue with publish even if schema update fails
        }
      }

      const payload = {
        name: frameworkName,
        stepOneData,
        stepTwoData,
        stepThreeData,
        stepFourData: {
          ...stepFourData,
          responseFields: filteredData, // Use filtered fields
          socketId, // Add socket ID to payload
        },
      };

      publishFrameworkMutation.mutate(payload, {
        onSuccess: () => {
          // Don't immediately show success - wait for socket confirmation
          setShowSuccessScreen(true);
          setIsPublishCompleted(false); // Keep loading state
          // Store the framework ID for later use (this is what's used in the dropdown)
          if (stepTwoData.frameworkId) {
            setCreatedFrameworkValue(stepTwoData.frameworkId);
          }
        },
        onError: (error) => {
          console.error('Publish error:', error);
          addNotification({
            type: 'error',
            message:
              error?.response?.data?.error_message ||
              error?.message ||
              'Failed to publish framework',
          });
        },
      });
    }
  }, [
    currentStep,
    totalSteps,
    validateStep,
    markStepCompleted,
    setCurrentStep,
    createFrameworkMutation,
    saveFrameworkDraftMutation,
    publishFrameworkMutation,
    submitControlsMutation,
    updateSchemaMutation,
    frameworkName,
    stepOneData,
    stepTwoData,
    stepThreeData,
    stepFourData,
    updateStepTwoData,
    updateStepFourData,
    updateStepOneData,
    resetWizard,
    onClose,
    setShowSuccessScreen, // add this
    stepThreeProceedBlocked, // add this
    socketData?.socket?.id,
    setIsPublishCompleted,
  ]);

  const handleClose = useCallback(() => {
    // Always show discard modal, regardless of framework state
    setIsDiscardModalOpen(true);
  }, []);

  const handleDiscardConfirm = useCallback(() => {
    if (stepTwoData.frameworkId) {
      // Delete the framework via API
      deleteFrameworkMutation.mutate(stepTwoData.frameworkId, {
        onSuccess: () => {
          setIsDiscardModalOpen(false);
          resetWizard();
          closeWizard();
          onClose?.();
        },
        onError: () => {
          // Even if delete fails, still close the wizard
          setIsDiscardModalOpen(false);
          resetWizard();
          closeWizard();
          onClose?.();
        },
      });
    } else {
      // No framework to delete, just close
      setIsDiscardModalOpen(false);
      resetWizard();
      closeWizard();
      onClose?.();
    }
  }, [
    stepTwoData.frameworkId,
    deleteFrameworkMutation,
    resetWizard,
    closeWizard,
    onClose,
  ]);

  const handleDiscardCancel = useCallback(() => {
    setIsDiscardModalOpen(false);
  }, []);

  const handleStartAssessment = useCallback(() => {
    const {
      createdFrameworkValue,
      pendingSessionTitle,
      resetWizard,
      closeWizard,
    } = useFrameworkWizardStore.getState();

    // Reset and close the wizard
    resetWizard();
    closeWizard();
    onClose?.();

    // Open the upload dialog with prefilled data
    // We need to pass this information back to the parent component
    if (onClose && createdFrameworkValue) {
      // Use a timeout to ensure the wizard is closed before opening upload dialog
      setTimeout(() => {
        // We'll emit a custom event that the parent can listen to
        const event = new CustomEvent('openGapAssessmentWithFramework', {
          detail: {
            sessionTitle: pendingSessionTitle,
            frameworkValue: createdFrameworkValue,
          },
        });
        window.dispatchEvent(event);
      }, 100);
    }
  }, [onClose]);

  // Auto-save draft functionality
  const handleSaveDraft = useCallback(() => {
    const payload = {
      name: frameworkName,
      stepOneData,
      stepTwoData,
      stepThreeData,
      stepFourData,
    };

    saveFrameworkDraftMutation.mutate(payload);
  }, [
    saveFrameworkDraftMutation,
    frameworkName,
    stepOneData,
    stepTwoData,
    stepThreeData,
    stepFourData,
  ]);

  // Socket listener for framework published event
  useEffect(() => {
    if (!socketData?.socket) return;

    const handleFrameworkPublished = (data: {
      success: boolean;
      message: string;
    }) => {
      if (data.success) {
        setIsPublishCompleted(true);
      } else {
        addNotification({
          type: 'error',
          title: 'Framework Publishing Failed',
          message: data.message || 'Failed to publish framework',
        });
        // Reset to allow retry
        setShowSuccessScreen(false);
        setIsPublishCompleted(false);
      }
    };

    socketData.socket.on(
      'custom:framework:published',
      handleFrameworkPublished
    );

    return () => {
      socketData.socket?.off(
        'custom:framework:published',
        handleFrameworkPublished
      );
    };
  }, [socketData?.socket]);

  // Compute file upload errors and valid file for StepTwo
  const files = stepTwoData.files || [];
  const hasValidFile = files.some((file) => file.status === 'success');
  const hasFileErrors = files.some(
    (file) =>
      file.status === 'too_large' ||
      file.status === 'too_many_files' ||
      file.status === 'duplicate_files' ||
      file.status === 'failed'
  );
  const isProceedBlocked =
    currentStep === 2 && (!hasValidFile || hasFileErrors);

  return (
    <>
      {showSuccessScreen ? (
        <SuccessScreen
          isLoading={!isPublishCompleted}
          onViewAgents={() => {
            resetWizard();
            onClose?.();
          }}
          onStartAssessment={handleStartAssessment}
        />
      ) : (
        <div className="w-full h-full bg-[#FEFEFE] rounded-lg border border-[#E4E4E7] shadow-lg overflow-hidden">
          <HeaderSection
            currentStep={currentStep}
            totalSteps={totalSteps}
            onStepClick={handleStepClick}
            onProceed={handleProceed}
            onClose={handleClose}
            isLoading={
              createFrameworkMutation.isPending ||
              saveFrameworkDraftMutation.isPending ||
              publishFrameworkMutation.isPending ||
              submitControlsMutation.isPending ||
              updateSchemaMutation.isPending
            }
            onSaveDraft={handleSaveDraft}
            isSavingDraft={saveFrameworkDraftMutation.isPending}
            isProceedBlocked={
              isProceedBlocked || (currentStep === 3 && stepThreeProceedBlocked)
            }
          />
          <Layout>{renderCurrentStep()}</Layout>
        </div>
      )}
      {/* Discard Modal */}
      <DiscardAgentModal
        isOpen={isDiscardModalOpen}
        onClose={handleDiscardCancel}
        onDiscard={handleDiscardConfirm}
        isDiscarding={deleteFrameworkMutation.isPending}
      />
    </>
  );
}
